'use client';

import { createClient } from '@/lib/supabase/client';

export interface CustomerData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  apartment?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  subscribeMarketing?: boolean;
}

export interface OrderItemData {
  productId?: string;
  productName: string;
  productImage?: string;
  variant?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface CreateOrderInput {
  customer: CustomerData;
  items: OrderItemData[];
  shippingMethod: 'standard' | 'express' | 'overnight';
  shippingCost: number;
  subtotal: number;
  total: number;
}

export interface OrderResult {
  orderId: string;
  orderNumber: string;
  customerId: string;
}

export const orderService = {
  async createOrder(input: CreateOrderInput): Promise<OrderResult | null> {
    const supabase = createClient();

    try {
      // 1. Upsert customer by email
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .insert({
          email: input.customer.email,
          first_name: input.customer.firstName,
          last_name: input.customer.lastName,
          phone: input.customer.phone || null,
          address: input.customer.address || null,
          apartment: input.customer.apartment || null,
          city: input.customer.city || null,
          state: input.customer.state || null,
          zip: input.customer.zip || null,
          country: input.customer.country || 'United States',
          subscribe_marketing: input.customer.subscribeMarketing ?? false,
        })
        .select('id')
        .single();

      if (customerError) {
        console.error('Error creating customer:', customerError.message);
        return null;
      }

      // 2. Generate order number
      const orderNumber =
        'LB-' + Math.floor(Math.random() * 90000 + 10000).toString().padStart(5, '0');

      // 3. Create order
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert({
          order_number: orderNumber,
          customer_id: customerData.id,
          status: 'pending',
          shipping_method: input.shippingMethod,
          shipping_cost: input.shippingCost,
          subtotal: input.subtotal,
          total: input.total,
        })
        .select('id, order_number')
        .single();

      if (orderError) {
        console.error('Error creating order:', orderError.message);
        return null;
      }

      // 4. Create order items
      const orderItems = input.items.map((item) => ({
        order_id: orderData.id,
        product_id: item.productId || null,
        product_name: item.productName,
        product_image: item.productImage || null,
        variant: item.variant || null,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.totalPrice,
      }));

      const { error: itemsError } = await supabase.from('order_items').insert(orderItems);

      if (itemsError) {
        console.error('Error creating order items:', itemsError.message);
        // Order was created, still return success
      }

      return {
        orderId: orderData.id,
        orderNumber: orderData.order_number,
        customerId: customerData.id,
      };
    } catch (err: any) {
      console.error('orderService.createOrder failed:', err.message);
      return null;
    }
  },

  async getOrderByNumber(orderNumber: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*, order_items(*), customers(*)')
        .eq('order_number', orderNumber)
        .maybeSingle();

      if (error) {
        console.error('Error fetching order:', error.message);
        return null;
      }
      return data;
    } catch (err: any) {
      console.error('orderService.getOrderByNumber failed:', err.message);
      return null;
    }
  },
};
